import { useState, useEffect } from 'react';
import { Save, Folder, Activity, Info } from 'lucide-react';
import Modal from '../common/Modal';
import type { ApplicationGroup } from '../../services';
import { usePermissions } from '../../hooks/usePermissions';

interface ApplicationGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    group?: ApplicationGroup;
    mode: 'add' | 'edit';
}

const ApplicationGroupModal = ({ isOpen, onClose, onSave, group, mode }: ApplicationGroupModalProps) => {
    const { canCreate, canUpdate } = usePermissions();
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        description: '',
    });

    useEffect(() => {
        if (isOpen) {
            if (group) {
                setFormData({
                    id: group.id,
                    name: group.name || '',
                    description: group.description || '',
                });
            } else {
                setFormData({
                    id: '',
                    name: '',
                    description: '',
                });
            }
        }
    }, [isOpen, group]);

    const handleChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    const footer = (
        <div className="flex items-center justify-end space-x-3">
            <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
            </button>
            {(mode === 'add' ? canCreate : canUpdate) && (
                <button form="app-group-form" type="submit" className="btn-primary inline-flex items-center space-x-2">
                    <Save className="w-4 h-4" />
                    <span>{mode === 'add' ? 'Add Group' : 'Save Changes'}</span>
                </button>
            )}
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={mode === 'add' ? 'Add Application Group' : `Edit Group: ${formData.name}`}
            size="md"
            footer={footer}
        >
            <div className="space-y-6">
                <form id="app-group-form" onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Group Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Folder className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="e.g., Collaboration, Business"
                                className="input-base w-full pl-10"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="e.g., General collaboration tools and services"
                            className="input-base w-full resize-none"
                            rows={3}
                        />
                    </div>
                </form>

                {mode === 'edit' && group && group.applications && (
                    <div className="border-t border-gray-100 pt-6">
                        <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                            <Activity className="w-4 h-4 mr-2 text-primary-500" />
                            Assigned Applications ({group.applications.length})
                        </h4>
                        <div className="bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
                            {group.applications.length > 0 ? (
                                <ul className="divide-y divide-gray-200 max-h-40 overflow-y-auto">
                                    {group.applications.map(app => (
                                        <li key={app.id} className="px-4 py-2 text-sm text-gray-700 flex justify-between items-center bg-white">
                                            <span>{app.name}</span>
                                            {app.category && (
                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                                    {app.category}
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="p-4 text-center text-sm text-gray-500 italic flex flex-col items-center">
                                    <Info className="w-8 h-8 text-gray-300 mb-2" />
                                    No applications assigned to this group.
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            To add applications to this group, edit the specific application and select this group.
                        </p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ApplicationGroupModal;
